import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type AbilitiesService from 'ember-can/services/abilities';
import { task } from 'ember-concurrency';
import { resource, use } from 'ember-resources';
import ViewProfileLink from 'garaje/components/visitors/profiles/view-profile-link';
import type AjaxFetchService from 'garaje/services/ajax-fetch';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type LoggerService from 'garaje/services/logger';
import type StateService from 'garaje/services/state';
import urlBuilder from 'garaje/utils/url-builder';

interface IdScanningCardSignature {
  Args: {
    /** The linked visitor profile, or null/undefined when the visitor has none. */
    visitorProfileId?: string | null;
    /** Entry/invite id, forwarded for back navigation. */
    sourceId: string;
    /** 'entry' | 'invite' — the surface the link is returning to. */
    from: string;
  };
  Element: HTMLDivElement;
}

interface IdentityDocumentsResponse {
  data?: unknown[];
}

// Only existence is in question, so ask for the smallest page the endpoint serves.
const EXISTENCE_PROBE_QUERY = '?page[limit]=1';

/**
 * One existence check against a profile's identity documents. `found` stays false while
 * the request is in flight and on failure, so every unresolved state reads as "no
 * document" — the endpoint 5xxes when the directories gRPC hop is unhealthy, and a card
 * promising a document nobody could confirm is worse than no card.
 */
class IdentityDocumentProbe {
  @tracked found = false;

  constructor(
    private ajaxFetch: AjaxFetchService,
    private logger: LoggerService,
    private profileId: string,
  ) {
    void this.load.perform();
  }

  load = task(async () => {
    const url = `${urlBuilder.v3.visitorProfiles.identityDocuments(this.profileId)}${EXISTENCE_PROBE_QUERY}`;

    try {
      const response = await this.ajaxFetch.request<IdentityDocumentsResponse>(url);
      this.found = (response.data?.length ?? 0) > 0;
    } catch (e: unknown) {
      this.logger.warn('[IdScanningCard] identity-document lookup failed:', e as Error);
    }
  });
}

/**
 * "ID Scanning" block in the entry/invite Details sidebar, styled after
 * LegalDocumentActivity. Renders only once the visitor's identity document is
 * confirmed present on the profile — the whole point of the link.
 */
export default class IdScanningCard extends Component<IdScanningCardSignature> {
  @service declare abilities: AbilitiesService;
  @service declare ajaxFetch: AjaxFetchService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare logger: LoggerService;
  @service declare state: StateService;

  /**
   * The per-location "save ID document on profile" toggle (VIS-17395). It governs the
   * write path only, so it is necessary but not sufficient — the probe settles whether a
   * document is actually there.
   * Undefined until the visitors route resolves the config — fail-closed.
   */
  get storesIdentityDocumentOnProfile(): boolean {
    return Boolean(this.state.currentLocation?.idScanConfiguration?.storeIdentityDocumentOnProfile);
  }

  /** Non-null by the time the card renders; `showCard` gates on it. */
  get profileId(): string {
    return this.args.visitorProfileId ?? '';
  }

  /**
   * Everything decidable without a request. The probe hangs off this so pages that could
   * never render the card cost no directories round-trip.
   */
  get isEligible(): boolean {
    return (
      Boolean(this.args.visitorProfileId) &&
      this.storesIdentityDocumentOnProfile &&
      this.featureFlags.isEnabled('vis-profile-identity-tab') &&
      this.featureFlags.isEnabled('vis-visitor-profile-show-rrc') &&
      this.abilities.can('read visitor-profile') &&
      this.abilities.can('enable id-scanning') &&
      // Same gate the Identity tab itself uses. Without it this link is a dead affordance:
      // the profile route clamps ?tab=identity back to visits for an unauthorized user, and
      // the card's presence would still disclose that the visitor has an ID on file.
      this.abilities.can('read-profile-identity-documents id-scanning')
    );
  }

  /**
   * Re-runs when eligibility flips, which it does once the id-scan configuration resolves
   * — the visitors route loads that without awaiting it, so a one-shot read at
   * construction would decide before the answer arrived.
   */
  @use probe = resource<IdentityDocumentProbe | null>(() => {
    const profileId = this.args.visitorProfileId;
    if (!this.isEligible || !profileId) return null;

    return new IdentityDocumentProbe(this.ajaxFetch, this.logger, profileId);
  });

  /**
   * The card states "saved to profile", so presence has to be a fact about the profile
   * rather than an inference from the location toggle: the toggle decides whether *new*
   * scans are stored and says nothing about a visitor scanned before it was switched on,
   * or at another location.
   */
  get showCard(): boolean {
    return this.isEligible && Boolean(this.probe?.found);
  }

  <template>
    {{#if this.showCard}}
      <div class="border-b pb-4 mb-4" data-test-id-scanning-card ...attributes>
        <h4 class="text-sm capitalize">ID scanning</h4>
        <ul class="list-none p-0 m-0 leading-normal mt-2">
          <li class="mb-1 ml-6 text-sm text-carbon-70 list-inside" style="text-indent: -1.5rem">
            <img src="/assets/images/checkmark-small-gray.svg" class="mr-2" alt="" role="presentation" />
            Identity document
            <span class="text-carbon-50">
              - saved to profile
            </span>
          </li>
        </ul>
        <div class="mt-3">
          <ViewProfileLink
            @from={{@from}}
            @sourceId={{@sourceId}}
            @visitorId={{this.profileId}}
            @tab="identity"
            @label="View Identity document"
            @variant="text"
          />
        </div>
      </div>
    {{/if}}
  </template>
}
